/**
 * Page Performance — the page-open state machine (pure, no React/DOM).
 *
 * Everything time-related is injected (clock, timers, the "next frame"
 * scheduler, id generator), so the whole rule set runs under `node --test`
 * with a fake clock. `PagePerfTracker.jsx` wires it to the browser.
 *
 * Rules (spec "How Page Performance measures things"):
 *
 *   - An open starts at the route change (in-app) or at navigation start,
 *     t = 0 (full load). It SETTLES when every `api` call it started has
 *     finished, nothing new started for 500 ms, and the next frame was
 *     painted. The load time is measured to that frame — the frame painted
 *     after the last call finished; the 500 ms quiet window only confirms it.
 *   - 20 s cap: an open still unsettled 20 s after its start is reported
 *     with `settled: false`.
 *   - Redirect absorption: a route change < 300 ms after an unsettled open's
 *     start, before any of its calls finished, is a redirect, not a page
 *     open — the open is re-keyed to the new route and keeps its start/kind.
 *   - Boot hand-over (full loads): `/` is a pass-through boot route, never a
 *     page. A full load that starts there, or on a route that redirects as
 *     soon as the boot calls (e.g. `/auth/me`) answer, keeps `kind: 'full'`
 *     and its t = 0 start and is handed to the first real route. For the
 *     full load the redirect window is also open < 300 ms after the latest
 *     call finished (the role redirect follows `/auth/me`), and always while
 *     it is still on `/`. A redirect to a sign-in / public route drops it —
 *     no full load is reported.
 *   - Abandoned: the person navigated away (or to a sign-in / public route),
 *     the page was hidden, or the measuring gate closed before the open
 *     settled. The open is REPORTED with `settled: false, abandoned: true`
 *     and `loadMs` = now − start. (`settled: false` without `abandoned` is
 *     the 20 s cap.)
 *   - Dropped, never reported: an open that started while the document was
 *     hidden or after it had been hidden (background-tab timings are
 *     meaningless — `dropOpen()`), an open the browser never painted (the
 *     frame watchdog below), and an open still on a boot / public route.
 *   - API calls: only calls STARTED in the open window count. Up to 40 are
 *     listed; the counts cover all of them. A call failed on a server error
 *     (5xx), a timeout (408), too many requests (429) or no reply (0) — a
 *     permission or not-found reply (403, 404 …) does not count
 *     (`isFailedStatus`). A cancelled call (the page aborted it) is counted
 *     but neither listed nor failed. In-flight time is the union of
 *     the intervals in which ≥ 1 call was running.
 *   - CLS / INP accumulate over the page session (open → next route change
 *     or page hide) and are emitted as an `end` record keyed by the load id,
 *     but only for a load that was actually reported. CLS is null when the
 *     browser cannot measure it. INP is the slowest `event` entry; 0 when
 *     interactions happened but none was slow enough to be reported ("every
 *     interaction was fast"); null when there was no interaction or the
 *     browser cannot measure it.
 *
 * No method ever throws into the caller.
 */
import { isFailedStatus } from './call-status.js'
import { isBootRoute, isExcludedRoute, normalizeRoute } from './route-key.js'

export const QUIET_MS = 500
export const CAP_MS = 20_000
export const REDIRECT_MS = 300
/** "The next frame was painted" needs a painted frame. A page the browser is
 *  not painting (a hidden embedded view, a window it treats as not shown)
 *  can still report itself as `visible`; without a frame it would only ever
 *  reach the 20 s cap and be stored as a 20 s load, making a healthy page
 *  look critical. So: if a frame is asked for and none arrives within 1 s
 *  while timers run on time, twice in a row, the page is not being painted
 *  and the open is dropped. A watchdog that fires LATE means the main thread
 *  was busy (a slow page drawing) — that is exactly what we must keep, so it
 *  just waits again. */
export const FRAME_WATCH_MS = 1_000
/** An interaction this close to the end of a page session is the click
 *  that left the page — see `inpValue`. */
export const LEAVING_CLICK_MS = 1_000
const MAX_INTERACTION_TIMES = 100
const FRAME_WATCH_LATE_MS = 200
const FRAME_WATCH_MISSES = 2
export const MAX_CALLS = 40

const METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])

const clampInt = (v, min, max) => {
  const n = Math.round(Number(v))
  if (!Number.isFinite(n)) return min
  return Math.min(max, Math.max(min, n))
}

const optInt = (v, min, max) =>
  typeof v === 'number' && Number.isFinite(v) ? clampInt(v, min, max) : undefined

/** A support flag may be a boolean or a getter. */
const isOn = (flag) => (typeof flag === 'function' ? Boolean(safe(flag)) : Boolean(flag))

const safe = (fn) => {
  try {
    return fn()
  } catch {
    return undefined
  }
}

export function createOpenTracker(deps = {}) {
  const {
    now = () => 0,
    setTimer = () => null,
    clearTimer = () => {},
    frame = (fn) => setTimer(fn, 16),
    cancelFrame = (h) => clearTimer(h),
    newId = () => `${Date.now()}-${Math.random()}`,
    onLoad = () => {},
    onEnd = () => {},
    getFullLoadMetrics = () => ({}),
    resolveBytes = () => undefined,
    // Per-call first byte (Resource Timing responseStart − requestStart).
    resolveTtfb = () => undefined,
    // Can the browser measure CLS (`layout-shift`) / INP (`event`)? When it
    // cannot, the end record says null ("unknown"), never 0 ("measured,
    // nothing moved").
    clsSupported = true,
    inpSupported = true,
  } = deps

  /** The open being timed (null when none). */
  let open = null
  /** The page session CLS / INP belong to (null when none). */
  let session = null
  let disposed = false

  const clock = (at) => (typeof at === 'number' && Number.isFinite(at) ? at : now())

  function clearSettle(o) {
    if (o.frameHandle != null) safe(() => cancelFrame(o.frameHandle))
    if (o.quietHandle != null) safe(() => clearTimer(o.quietHandle))
    if (o.frameWatchHandle != null) safe(() => clearTimer(o.frameWatchHandle))
    o.frameHandle = null
    o.quietHandle = null
    o.frameWatchHandle = null
    o.candidateAt = null
  }

  /** Watch for a frame that never comes (see FRAME_WATCH_MS). */
  function watchFrame(o, misses = 0) {
    const due = now() + FRAME_WATCH_MS
    o.frameWatchHandle = safe(() =>
      setTimer(() => {
        if (open !== o) return
        o.frameWatchHandle = null
        if (o.frameHandle == null) return // the frame arrived
        if (now() - due > FRAME_WATCH_LATE_MS) {
          // Fired late: the main thread was busy, not idle-without-painting.
          watchFrame(o, misses)
          return
        }
        if (misses + 1 >= FRAME_WATCH_MISSES) {
          drop() // not being painted — never report a fake 20 s load
          return
        }
        watchFrame(o, misses + 1)
      }, FRAME_WATCH_MS),
    )
  }

  function closeOpen(o) {
    clearSettle(o)
    if (o.capHandle != null) safe(() => clearTimer(o.capHandle))
    o.capHandle = null
    if (open === o) open = null
  }

  function endSession(at) {
    const s = session
    session = null
    if (!s || !s.reported) return
    const endAt = clock(at)
    safe(() =>
      onEnd({
        id: s.id,
        cls: isOn(clsSupported) ? Math.min(10, Math.max(0, Math.round(s.cls * 10_000) / 10_000)) : null,
        inpMs: inpValue(s, endAt),
      }),
    )
  }

  /** INP for the end record: slowest reported interaction; 0 when there
   *  were interactions but all were below the reporting floor; null when
   *  there was none or `event` timing is unsupported.
   *
   *  The click that LEAVES the page (a link to the next page) is seen here
   *  as an interaction, but its Event Timing entry only arrives after the
   *  next paint — by then this session has ended. So an interaction in the
   *  last second before the end never counts towards "every interaction was
   *  fast": without that, every page left by a click would report 0 even
   *  when the click itself was slow. */
  function inpValue(s, endAt) {
    if (!isOn(inpSupported)) return null
    if (s.inp != null) return clampInt(s.inp, 0, 60_000)
    const settledInteractions =
      s.olderInteractions + s.interactionTimes.filter((t) => endAt - t >= LEAVING_CLICK_MS).length
    return settledInteractions > 0 ? 0 : null
  }

  /** Close the open WITHOUT reporting it (and forget its session). */
  function drop() {
    if (!open) return
    const o = open
    closeOpen(o)
    // The load was never reported, so its session has nothing to send.
    if (session && session.id === o.id) session = null
  }

  /** Report the open in progress as abandoned (see the rules above). */
  function abandon(at) {
    if (!open) return
    report(open, 'abandoned', clock(at))
  }

  /** Arm settle detection: in-flight = 0 → next frame (candidate settle
   *  time) → 500 ms with nothing new started → report. */
  function armSettle(o) {
    clearSettle(o)
    if (o.inflight.size > 0) return
    const quietFrom = o.lastActivityAt
    o.frameHandle = safe(() =>
      frame(() => {
        if (open !== o) return
        o.frameHandle = null
        if (o.frameWatchHandle != null) safe(() => clearTimer(o.frameWatchHandle))
        o.frameWatchHandle = null
        o.candidateAt = now()
        const wait = Math.max(0, quietFrom + QUIET_MS - now())
        o.quietHandle = safe(() =>
          setTimer(() => {
            if (open !== o) return
            o.quietHandle = null
            if (o.inflight.size === 0 && o.candidateAt != null) report(o, 'settled', o.candidateAt)
          }, wait),
        )
      }),
    )
    if (o.frameHandle != null) watchFrame(o)
  }

  /** Close the open and report it. `outcome`: 'settled' | 'capped' |
   *  'abandoned'. An open still on a boot / sign-in / public route is never
   *  reported (it is not a page). */
  function report(o, outcome, endAt) {
    closeOpen(o)
    if (isExcludedRoute(o.route)) {
      if (session && session.id === o.id) session = null
      return
    }
    const settled = outcome === 'settled'
    const loadMs =
      outcome === 'capped'
        ? clampInt(Math.min(endAt - o.startAt, CAP_MS), 0, 120_000)
        : clampInt(endAt - o.startAt, 0, 120_000)
    let inFlight = o.inflightUnion
    if (o.inflight.size > 0 && o.inflightSince != null) {
      inFlight += Math.max(0, o.startAt + loadMs - o.inflightSince)
    }
    const calls = []
    for (const c of o.calls) {
      if (!c.done || c.cancelled || !c.listed) continue
      let bytes = c.bytes
      if (c.ref != null) {
        const rt = safe(() => resolveBytes(c.ref))
        if (typeof rt === 'number' && Number.isFinite(rt)) bytes = rt
      }
      const call = {
        method: c.method,
        path: c.path,
        status: clampInt(c.status, 0, 599),
        ms: clampInt(c.ms, 0, 120_000),
      }
      const b = optInt(bytes, 0, Number.MAX_SAFE_INTEGER)
      if (b !== undefined) call.bytes = b
      if (c.ref != null) {
        const ttfb = optInt(safe(() => resolveTtfb(c.ref)), 0, 120_000)
        if (ttfb !== undefined) call.ttfb = ttfb
      }
      calls.push(call)
    }
    const record = {
      id: o.id,
      route: o.route,
      kind: o.kind,
      startAt: o.startAt,
      loadMs,
      settled,
      ...(outcome === 'abandoned' ? { abandoned: true } : {}),
      apiCount: clampInt(o.apiCount, 0, 1000),
      apiInFlightMs: clampInt(Math.min(inFlight, loadMs), 0, 120_000),
      failedCount: clampInt(o.failedCount, 0, 1000),
      calls,
    }
    if (o.kind === 'full') {
      const m = safe(() => getFullLoadMetrics(o.startAt + loadMs)) || {}
      for (const key of ['ttfbMs', 'fcpMs', 'lcpMs']) {
        const v = optInt(m[key], 0, 120_000)
        if (v !== undefined) record[key] = v
      }
    }
    if (session && session.id === o.id) session.reported = true
    safe(() => onLoad(record))
  }

  function begin(route, kind, startAt) {
    const o = {
      id: newId(),
      route,
      kind: kind === 'full' ? 'full' : 'spa',
      startAt,
      seenAt: Math.max(startAt, now()),
      inflight: new Map(),
      calls: [],
      apiCount: 0,
      failedCount: 0,
      anyEnded: false,
      lastEndAt: null,
      // A full load may still be handed over by a boot redirect.
      bootRedirectable: kind === 'full',
      inflightSince: null,
      inflightUnion: 0,
      lastActivityAt: Math.max(startAt, now()),
      frameHandle: null,
      frameWatchHandle: null,
      quietHandle: null,
      candidateAt: null,
      capHandle: null,
    }
    open = o
    session = {
      id: o.id,
      startAt,
      cls: 0,
      inp: null,
      // Times of the most recent interactions (bounded) + a count of older ones.
      interactionTimes: [],
      olderInteractions: 0,
      reported: false,
    }
    o.capHandle = safe(() =>
      setTimer(() => {
        if (open === o) report(o, 'capped', now())
      }, Math.max(0, startAt + CAP_MS - now())),
    )
    armSettle(o)
  }

  /** Is a route change at `t` a redirect of the open (absorbed), rather
   *  than the person moving on? A full load starts at t = 0, before the app
   *  even ran, so windows are measured from when the tracker saw the open. */
  function isRedirect(o, t) {
    if (!o.anyEnded && t - o.seenAt < REDIRECT_MS) return true
    if (!o.bootRedirectable) return false
    if (isBootRoute(o.route)) return true
    return o.lastEndAt != null && t - o.lastEndAt < REDIRECT_MS
  }

  return {
    /** A route change (or the first route of a full load). */
    start({ route, kind = 'spa', at } = {}) {
      safe(() => {
        if (disposed) return
        const t = clock(at)
        const key = normalizeRoute(route)
        const excluded = isExcludedRoute(key)
        if (open && isRedirect(open, t)) {
          if (isBootRoute(key)) {
            open.route = key // still booting
          } else if (excluded) {
            drop() // bounced to sign-in / a public page: not a page open
          } else {
            open.route = key
          }
          return
        }
        abandon(t)
        endSession(t)
        // A full load may begin on the boot route `/`; any other sign-in /
        // public route is never measured.
        if (excluded && !(kind === 'full' && isBootRoute(key))) return
        begin(key, kind, t)
      })
    },

    /** An `api` call started. Returns true when it belongs to the open. */
    requestStarted(id, { method, path, at, ref } = {}) {
      return (
        safe(() => {
          if (disposed || !open || id == null) return false
          const o = open
          const t = clock(at)
          const m = String(method || 'GET').toUpperCase()
          const call = {
            id,
            method: m,
            path: typeof path === 'string' ? path : '/',
            ref: ref ?? null,
            startedAt: t,
            done: false,
            cancelled: false,
            listed: o.calls.length < MAX_CALLS && METHODS.has(m),
            status: 0,
            ms: 0,
            bytes: undefined,
          }
          o.apiCount += 1
          // The boot hand-over is over once the full load sits on a real page
          // and that page starts its own calls after a reply: from here a
          // route change is the person moving on, never a boot redirect.
          if (o.bootRedirectable && o.anyEnded && !isBootRoute(o.route)) o.bootRedirectable = false
          if (o.calls.length < MAX_CALLS) o.calls.push(call)
          if (o.inflight.size === 0) o.inflightSince = t
          o.inflight.set(id, call)
          o.lastActivityAt = Math.max(o.lastActivityAt, t)
          clearSettle(o)
          return true
        }) === true
      )
    },

    /** True while the call is still counted as in flight by the open. */
    isPending(id) {
      return Boolean(open && id != null && open.inflight.has(id))
    },

    /** An `api` call finished (reply, error, no reply or cancellation). */
    requestEnded(id, { status = 0, bytes, at, cancelled = false } = {}) {
      safe(() => {
        if (disposed || !open || id == null) return
        const o = open
        const call = o.inflight.get(id)
        if (!call) return
        const t = clock(at)
        o.inflight.delete(id)
        call.done = true
        call.cancelled = Boolean(cancelled)
        call.status = clampInt(status, 0, 599)
        call.ms = Math.max(0, t - call.startedAt)
        call.bytes = bytes
        if (!call.cancelled) {
          o.anyEnded = true
          o.lastEndAt = t
          if (isFailedStatus(call.status)) o.failedCount += 1
        }
        if (o.inflight.size === 0 && o.inflightSince != null) {
          o.inflightUnion += Math.max(0, t - o.inflightSince)
          o.inflightSince = null
        }
        o.lastActivityAt = Math.max(o.lastActivityAt, t)
        armSettle(o)
      })
    },

    /** A `layout-shift` entry without recent input. */
    layoutShift(value, at) {
      safe(() => {
        if (!session || !(value > 0)) return
        if (typeof at === 'number' && at < session.startAt) return
        session.cls += value
      })
    },

    /** An `event` entry with an interactionId (its duration, ms). */
    interaction(durationMs, at) {
      safe(() => {
        if (!session || !(durationMs >= 0)) return
        if (typeof at === 'number' && at < session.startAt) return
        session.inp = session.inp == null ? durationMs : Math.max(session.inp, durationMs)
      })
    },

    /** Any interaction (pointerdown / keydown / first-input), reported or
     *  not by Event Timing — tells "every interaction was fast" (INP 0) from
     *  "no interaction" (null). */
    interactionSeen(at) {
      safe(() => {
        if (!session) return
        if (typeof at === 'number' && at < session.startAt) return
        session.interactionTimes.push(clock(at))
        if (session.interactionTimes.length > MAX_INTERACTION_TIMES) {
          session.interactionTimes.shift()
          session.olderInteractions += 1
        }
      })
    },

    /** The page was hidden / left, or the measuring gate is closing: report
     *  an open in progress as abandoned and end the page session (sends the
     *  `end` of a reported load). */
    documentHidden(at) {
      safe(() => {
        abandon(at)
        endSession(at)
      })
    },

    /** Same as `documentHidden` — named for the gate / control callers. */
    abandonOpen(at) {
      safe(() => {
        abandon(at)
        endSession(at)
      })
    },

    /** Drop the open in progress WITHOUT reporting it: it started while the
     *  document was hidden, or the identity behind the tab changed. */
    dropOpen() {
      safe(() => drop())
    },

    /** Forget the page session too (nothing more will be sent for it). */
    forgetSession() {
      session = null
    },

    /** Stop everything without reporting (unmount / StrictMode remount). */
    dispose() {
      safe(() => {
        disposed = true
        if (open) closeOpen(open)
        open = null
        session = null
      })
    },

    /** Read-only snapshot for tests. */
    snapshot() {
      return {
        open: open
          ? { id: open.id, route: open.route, kind: open.kind, startAt: open.startAt, inflight: open.inflight.size }
          : null,
        session: session ? { id: session.id, cls: session.cls, inp: session.inp, reported: session.reported } : null,
      }
    },
  }
}
